import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ObjectPhoto } from '../shared/ObjectPhoto';
import { ApiError } from '../../lib/api';
import { dictionariesApi, type DictionaryValue } from '../../lib/dictionariesApi';
import { formatCellValue, inputPropsFor, parseEditedValue } from '../../lib/fieldFormat';
import { layersApi, type LayerDetail, type LayerFeature, type LayerFieldDetail } from '../../lib/layersApi';
import { useMapContextOptional } from '../../map/MapContext';

export function LayerTable({
  layerId,
  onGoToObject,
}: {
  layerId: string;
  onGoToObject?: (feature: LayerFeature) => void;
}) {
  // Optional: this table is also reused on the standalone registry page, which
  // has no map mounted — there's simply nothing to sync a deletion into there.
  const removeFeatureFromMap = useMapContextOptional()?.removeFeatureFromMap;
  const [layer, setLayer] = useState<LayerDetail | null>(null);
  // Backend-computed: workspace_admin/gis_admin always; operator only for
  // layers they (or a group they're in) hold an explicit grant on.
  const canEdit = layer?.can_edit_features ?? false;
  const [features, setFeatures] = useState<LayerFeature[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // Rendering every fetched feature as a <tr> at once is what actually froze the tab on
  // a layer with thousands of rows — the fetch itself is already capped server-side
  // (see maps-service's _MAX_FEATURES_RESPONSE), but even 5 000 unvirtualized DOM rows
  // is enough to choke the browser. Paginating only the render (not the fetch or the
  // search, which still run over every loaded feature) keeps the DOM small without
  // pulling in a virtualization library for what's otherwise a one-file fix.
  const PAGE_SIZE = 200;
  const [page, setPage] = useState(0);

  const [editingCell, setEditingCell] = useState<{ featureId: string; code: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  // Изменения копятся тут до нажатия «Сохранить» — featureId -> { code: value }.
  const [pending, setPending] = useState<Record<string, Record<string, unknown>>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    Promise.all([layersApi.getLayer(layerId), layersApi.getFeatures(layerId)])
      .then(([detail, fc]) => {
        if (cancelled) return;
        setLayer(detail);
        setFeatures(fc.features);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof ApiError ? err.message : 'Не удалось загрузить данные слоя.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    setPending({});
    return () => {
      cancelled = true;
    };
  }, [layerId]);

  const columns = useMemo(() => (layer?.fields ?? []).filter((f) => f.visible_in_table), [layer]);

  // Cache of dictionary_id -> its values, lazily fetched once a dictionary-typed
  // column shows up — needed to render the stored value id as "Магнит" instead
  // of a bare UUID, and to populate the edit dropdown's options.
  const [dictValuesByDict, setDictValuesByDict] = useState<Record<string, DictionaryValue[]>>({});
  useEffect(() => {
    const dictIds = new Set(
      columns.filter((c) => c.data_type === 'dictionary' && c.dictionary_id).map((c) => c.dictionary_id as string),
    );
    for (const id of dictIds) {
      if (dictValuesByDict[id]) continue;
      dictionariesApi
        .listValues(id)
        .then((vals) => setDictValuesByDict((prev) => ({ ...prev, [id]: vals })))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  const effectiveValue = (feature: LayerFeature, code: string): unknown =>
    pending[feature.id]?.[code] ?? feature.properties[code];

  const filteredFeatures = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return features;
    return features.filter((f) =>
      columns.some((c) => formatCellValue(effectiveValue(f, c.code)).toLowerCase().includes(q)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features, columns, search, pending]);

  useEffect(() => setPage(0), [search, layerId]);

  const pageCount = Math.max(1, Math.ceil(filteredFeatures.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pagedFeatures = filteredFeatures.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const pendingFeatureCount = Object.keys(pending).length;
  const pendingFieldCount = Object.values(pending).reduce((n, f) => n + Object.keys(f).length, 0);

  const stageValue = (feature: LayerFeature, column: LayerFieldDetail, nextValue: unknown) => {
    const original = feature.properties[column.code] ?? null;
    setPending((prev) => {
      const next = { ...prev };
      const featureEdits = { ...(next[feature.id] ?? {}) };
      if (nextValue === original) {
        delete featureEdits[column.code];
      } else {
        featureEdits[column.code] = nextValue;
      }
      if (Object.keys(featureEdits).length === 0) delete next[feature.id];
      else next[feature.id] = featureEdits;
      return next;
    });
  };

  const startEdit = (feature: LayerFeature, column: LayerFieldDetail) => {
    if (!canEdit || column.data_type === 'boolean') return;
    setEditingCell({ featureId: feature.id, code: column.code });
    setEditError(null);
    const current = effectiveValue(feature, column.code);
    setEditValue(formatCellValue(current) === '—' ? '' : String(current));
  };

  const commitEdit = (feature: LayerFeature, column: LayerFieldDetail) => {
    const result = parseEditedValue(editValue, column.data_type);
    if (!result.ok) {
      setEditError(result.error);
      return;
    }
    stageValue(feature, column, result.value);
    setEditingCell(null);
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditError(null);
  };

  const handleDiscard = () => setPending({});

  const handleDeleteFeature = async (feature: LayerFeature) => {
    if (!window.confirm('Удалить объект? Это действие нельзя отменить.')) return;
    setDeletingId(feature.id);
    setSaveError(null);
    try {
      await layersApi.deleteFeature(layerId, feature.id);
      setFeatures((prev) => prev.filter((f) => f.id !== feature.id));
      removeFeatureFromMap?.(layerId, feature.id);
      setPending((prev) => {
        if (!prev[feature.id]) return prev;
        const next = { ...prev };
        delete next[feature.id];
        return next;
      });
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось удалить объект.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSetPhoto = async (feature: LayerFeature, fileId: string) => {
    try {
      const updated = await layersApi.setFeaturePhoto(layerId, feature.id, fileId);
      setFeatures((prev) => prev.map((f) => (f.id === feature.id ? { ...f, photo_file_id: updated.photo_file_id } : f)));
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось сохранить фото.');
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      for (const [featureId, fields] of Object.entries(pending)) {
        await layersApi.updateFeature(layerId, featureId, fields);
      }
      setFeatures((prev) =>
        prev.map((f) => (pending[f.id] ? { ...f, properties: { ...f.properties, ...pending[f.id] } } : f)),
      );
      setPending({});
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось сохранить изменения.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="p-6 text-center text-xs text-text-secondary">Загрузка…</p>;
  if (loadError) return <p className="p-6 text-center text-xs font-medium text-status-danger">{loadError}</p>;

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border-default p-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по таблице…"
          className="h-9 w-72 rounded-lg bg-bg-canvas px-3 text-xs text-text-primary outline-none placeholder:text-text-secondary"
        />
        <span className="text-[11px] text-text-secondary">
          {filteredFeatures.length.toLocaleString('ru-RU')} из {features.length.toLocaleString('ru-RU')}
        </span>
        {pageCount > 1 && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              className="flex h-7 items-center rounded-lg px-2 text-[11px] font-medium text-text-secondary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-40"
            >
              ← Назад
            </button>
            <span className="text-[11px] text-text-secondary">
              Стр. {currentPage + 1} из {pageCount}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={currentPage >= pageCount - 1}
              className="flex h-7 items-center rounded-lg px-2 text-[11px] font-medium text-text-secondary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-40"
            >
              Вперёд →
            </button>
          </div>
        )}
        {!canEdit && (
          <span className="text-[11px] text-text-secondary">
            У вас нет доступа к редактированию этого слоя
          </span>
        )}
        {saveError && <span className="text-[11px] font-medium text-status-danger">{saveError}</span>}

        {pendingFieldCount > 0 && (
          <div className="ml-auto flex items-center gap-3">
            <span className="text-[11px] text-text-secondary">
              Изменено полей: {pendingFieldCount} ({pendingFeatureCount}{' '}
              {pendingFeatureCount === 1 ? 'объект' : 'объектов'})
            </span>
            <button
              type="button"
              onClick={handleDiscard}
              disabled={saving}
              className="text-[11px] font-semibold text-text-secondary hover:text-text-primary"
            >
              Отменить
            </button>
            <button
              type="button"
              onClick={handleSaveAll}
              disabled={saving}
              className="flex h-8 items-center rounded-lg bg-action-primary px-3 text-[11px] font-medium text-text-inverse hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Сохраняем…' : 'Сохранить'}
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        {columns.length === 0 && (
          <p className="p-3 text-center text-xs text-text-secondary">
            У слоя нет полей, отмеченных «видно в таблице» — настройте семантику в редактировании слоя
          </p>
        )}
        <table className="w-full border-collapse text-left text-xs">
            <thead className="sticky top-0 z-10 bg-bg-surface-secondary">
              <tr>
                <th className="w-[60px] px-3 py-2 font-semibold text-text-secondary">Фото</th>
                {columns.map((c) => (
                  <th key={c.code} className="whitespace-nowrap px-3 py-2 font-semibold text-text-secondary">
                    {c.name}
                  </th>
                ))}
                <th className="w-10 px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {pagedFeatures.map((feature) => (
                <tr key={feature.id} className="border-b border-border-default hover:bg-bg-surface-secondary">
                  <td className="px-3 py-1.5">
                    <ObjectPhoto
                      fileId={feature.photo_file_id}
                      canEdit={canEdit}
                      size={40}
                      onChange={(fileId) => handleSetPhoto(feature, fileId)}
                    />
                  </td>
                  {columns.map((column) => {
                    const isEditing = editingCell?.featureId === feature.id && editingCell.code === column.code;
                    const isDirty = pending[feature.id]?.[column.code] !== undefined;
                    const value = effectiveValue(feature, column.code);

                    if (column.data_type === 'boolean') {
                      return (
                        <td key={column.code} className={`px-3 py-1.5 ${isDirty ? 'bg-action-primary/10' : ''}`}>
                          <input
                            type="checkbox"
                            checked={Boolean(value)}
                            disabled={!canEdit}
                            onChange={(e) => stageValue(feature, column, e.target.checked)}
                            className="size-[16px] rounded-[4px] accent-action-primary disabled:opacity-40"
                          />
                        </td>
                      );
                    }

                    if (column.data_type === 'dictionary') {
                      const dictId = column.dictionary_id ?? undefined;
                      const dictValues = dictId ? dictValuesByDict[dictId] : undefined;
                      const valueId = typeof value === 'string' ? value : null;
                      const matched = dictValues?.find((v) => v.id === valueId);
                      return (
                        <td key={column.code} className={`px-3 py-1.5 ${isDirty ? 'bg-action-primary/10' : ''}`}>
                          {isEditing ? (
                            <select
                              autoFocus
                              value={valueId ?? ''}
                              onChange={(e) => {
                                stageValue(feature, column, e.target.value || null);
                                setEditingCell(null);
                              }}
                              onBlur={() => setEditingCell(null)}
                              className="h-7 w-full rounded border border-action-primary bg-bg-surface px-1.5 text-xs text-text-primary outline-none"
                            >
                              <option value="">—</option>
                              {(dictValues ?? []).map((v) => (
                                <option key={v.id} value={v.id}>
                                  {v.name}
                                </option>
                              ))}
                            </select>
                          ) : matched ? (
                            <div className="flex items-center gap-1.5">
                              <Link
                                to={`/app/dictionaries/${dictId}/values/${matched.id}`}
                                className="font-medium text-action-primary hover:underline"
                              >
                                {matched.name}
                              </Link>
                              {canEdit && (
                                <button
                                  type="button"
                                  title="Изменить"
                                  onClick={() => setEditingCell({ featureId: feature.id, code: column.code })}
                                  className="text-text-secondary hover:text-text-primary"
                                >
                                  ✎
                                </button>
                              )}
                            </div>
                          ) : (
                            <span
                              onClick={() => canEdit && setEditingCell({ featureId: feature.id, code: column.code })}
                              className={canEdit ? 'cursor-pointer' : ''}
                            >
                              —
                            </span>
                          )}
                        </td>
                      );
                    }

                    const { type, step } = inputPropsFor(column.data_type);

                    return (
                      <td
                        key={column.code}
                        onClick={() => startEdit(feature, column)}
                        className={`relative px-3 py-1.5 text-text-primary ${canEdit ? 'cursor-pointer hover:bg-bg-canvas' : ''} ${
                          isDirty ? 'bg-action-primary/10' : ''
                        }`}
                      >
                        {isEditing ? (
                          <>
                            <input
                              autoFocus
                              type={type}
                              step={step}
                              value={editValue}
                              onChange={(e) => {
                                setEditValue(e.target.value);
                                setEditError(null);
                              }}
                              onBlur={() => commitEdit(feature, column)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitEdit(feature, column);
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              className={`h-7 w-full rounded border bg-bg-surface px-1.5 text-xs text-text-primary outline-none ${
                                editError ? 'border-status-danger' : 'border-action-primary'
                              }`}
                            />
                            {editError && (
                              <span className="absolute left-0 top-full z-20 mt-0.5 whitespace-nowrap rounded bg-status-danger px-1.5 py-0.5 text-[10px] font-medium text-text-inverse">
                                {editError}
                              </span>
                            )}
                          </>
                        ) : (
                          formatCellValue(value)
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1">
                      <Link
                        to={`/app/data/layers/${layerId}/objects/${feature.id}`}
                        title="Открыть карточку объекта"
                        className="flex size-7 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-surface-secondary hover:text-action-primary"
                      >
                        ▤
                      </Link>
                      {onGoToObject && (
                        <button
                          type="button"
                          onClick={() => onGoToObject(feature)}
                          title="Перейти к объекту"
                          className="flex size-7 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-surface-secondary hover:text-action-primary"
                        >
                          ➜
                        </button>
                      )}
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => handleDeleteFeature(feature)}
                          disabled={deletingId === feature.id}
                          title="Удалить объект"
                          className="flex size-7 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-surface-secondary hover:text-status-danger disabled:opacity-40"
                        >
                          {deletingId === feature.id ? '…' : '🗑'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredFeatures.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 2} className="py-8 text-center text-xs text-text-secondary">
                    Ничего не найдено
                  </td>
                </tr>
              )}
            </tbody>
        </table>
      </div>
    </div>
  );
}
